import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useAtom } from '~/observers/jotai'
import { authenticationAtom } from '~/modules/authentication/data'
import {
  getActingPersonalTrainerId,
  isActingAsPersonalTrainer,
} from '~/modules/authentication/utils'

import { AdminImpersonationBar } from './AdminImpersonationBar'
import { PageContainer } from './PageContainer'
import { TopBar } from './TopBar'
import { LayoutContextData } from './types'

export const LayoutContext = createContext<LayoutContextData>(
  {} as LayoutContextData,
)

export const LayoutProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const mainRef = useRef<HTMLDivElement>(null)
  const [session] = useAtom(authenticationAtom)
  const [hasScrolled, setHasScrolled] = useState<boolean>(false)
  const impersonationReady =
    session?.role !== 'admin' || isActingAsPersonalTrainer(session)
  const actingPersonalTrainerId = getActingPersonalTrainerId(session)

  const scrollToTop = useCallback(() => {
    mainRef.current?.scrollTo({
      top: 0,
    })
  }, [])

  useEffect(() => {
    const pageContainer = mainRef.current

    const handleScroll = () => {
      if (pageContainer) {
        setHasScrolled(pageContainer.scrollTop > 0)
      }
    }

    if (pageContainer) {
      mainRef.current.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (pageContainer) {
        pageContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  return (
    <PageContainer ref={mainRef}>
      <LayoutContext.Provider value={{ scrollToTop, hasScrolled }}>
        <TopBar />
        <main
          key={actingPersonalTrainerId ?? 'no-impersonation'}
          className='flex flex-1 bg-gpa-gray-50 overflow-hidden'
        >
          {impersonationReady ? (
            children
          ) : (
            <div className='flex flex-1 items-center justify-center px-6 text-center'>
              <div className='max-w-2xl space-y-2'>
                <h2 className='text-xl font-semibold'>
                  Escolha um personal trainer para visualizar esta área
                </h2>
                <p className='text-slate-600 text-sm'>
                  Use o seletor no rodapé para entrar no modo de impersonação e
                  navegar como o personal escolhido.
                </p>
              </div>
            </div>
          )}
        </main>
        <AdminImpersonationBar />
      </LayoutContext.Provider>
    </PageContainer>
  )
}
